import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { css } from '@emotion/core';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { above } from 'utils/breakpoint';
import { validatePasswordPolicy } from 'features/login/utils/passwordPolicy';
import { neutral500, neutral900 } from '@fever/ui';
import {
    mainButtonSolidStyle,
    errMsgWrapperStyle,
    textFieldDefaultStyle
} from 'features/login/styles/comman';

import BtnBackToAccountSetting from 'features/login/components/account/BtnBackToAccountSetting';
import ButtonWithLoadingEffect from 'features/common/ButtonWithLoadingEffect';

const BlockPasswordInput = forwardRef(
    ({ email, onSave = () => {}, needPrevPassword, showBackToAccountSettingBtn }, ref) => {
        const { t } = useTranslation();
        const [isSaving, setIsSaving] = useState(false);
        const [showPassword, setShowPassword] = useState(false);
        const [prevPassword, setPrevPassword] = useState('');
        const [prevPasswordErrMsg, setPrevPasswordErrMsg] = useState('');
        const [password, setPassword] = useState('');
        const [passwordErrMsg, setPasswordErrMsg] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [confirmPasswordErrMsg, setConfirmPasswordErrMsg] = useState('');

        useImperativeHandle(ref, () => ({ setPrevPasswordErrMsg, setPasswordErrMsg }));

        function handlePrevPasswordChange({ target: { value } }) {
            setPrevPassword(value);
            setPrevPasswordErrMsg('');
        }
        function handlePasswordChange({ target: { value } }) {
            setPassword(value);
            setPasswordErrMsg('');
        }
        function handleConfirmPasswordChange({ target: { value } }) {
            setConfirmPassword(value);
            setConfirmPasswordErrMsg('');
        }
        function validateInput() {
            let valid = true;
            // 舊密碼只檢查必填。它是使用者既有的密碼，可能建立於新政策之前（例如 6 碼），
            // 拿新政策去驗會把存量管理員擋在修改密碼流程之外。是否正確由 Firebase 判定。
            if (needPrevPassword && !prevPassword) {
                valid = false;
                setPrevPasswordErrMsg(i18n.t('label.error.required.pw'));
            }
            const passResult = validatePasswordPolicy(password);
            if (!passResult.valid) {
                valid = false;
            }
            setPasswordErrMsg(passResult.msg);
            // Timing attack 誤報：比對的是使用者自己輸入的兩個欄位（密碼／確認密碼），
            // 兩邊皆為使用者已知值，不存在可被推測的祕密。
            // nosemgrep: eslint.detect-possible-timing-attacks, nodejs_scan.javascript-crypto-rule-node_timing_attack
            if (confirmPassword !== password) {
                valid = false;
                setConfirmPasswordErrMsg(i18n.t('label.confirm_pass_not_same'));
            }
            return valid;
        }
        async function handleSavePassword() {
            if (validateInput()) {
                setIsSaving(true);
                await onSave({ prevPassword, password });
                setIsSaving(false);
            }
        }

        return (
            <div css={containerStyle}>
                <Typography variant="h5" css={titleStyle}>
                    {t('label.pw_setting')}
                </Typography>
                <Typography variant="subtitle1" css={descStyle}>
                    {t('label.account')}
                    {email}
                </Typography>
                {needPrevPassword && (
                    <>
                        <TextField
                            variant="filled"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            value={prevPassword}
                            onChange={handlePrevPasswordChange}
                            placeholder={t('placehoder.input_old_pw')}
                            css={[textFieldDefaultStyle, css(`margin-top: 24px;`)]}
                            error={Boolean(prevPasswordErrMsg)}
                        />
                        <div css={errMsgWrapperStyle}>
                            {Boolean(prevPasswordErrMsg) && (
                                <Typography variant="caption">{prevPasswordErrMsg}</Typography>
                            )}
                        </div>
                    </>
                )}
                <TextField
                    variant="filled"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={t(
                        needPrevPassword ? 'placehoder.input_new_pw' : 'placehoder.input_pw'
                    )}
                    css={[textFieldDefaultStyle, !needPrevPassword && css(`margin-top: 24px;`)]}
                    error={Boolean(passwordErrMsg)}
                />
                <div css={errMsgWrapperStyle}>
                    {Boolean(passwordErrMsg) && (
                        <Typography variant="caption">{passwordErrMsg}</Typography>
                    )}
                </div>
                <TextField
                    variant="filled"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    placeholder={t(
                        needPrevPassword
                            ? 'placehoder.input_new_pw_again'
                            : 'placehoder.input_pw_again'
                    )}
                    css={textFieldDefaultStyle}
                    error={Boolean(confirmPasswordErrMsg)}
                />
                <div css={errMsgWrapperStyle}>
                    {Boolean(confirmPasswordErrMsg) && (
                        <Typography variant="caption">{confirmPasswordErrMsg}</Typography>
                    )}
                </div>
                <FormControlLabel
                    value={showPassword}
                    control={
                        <Checkbox
                            color="primary"
                            onChange={({ target: { checked } }) => setShowPassword(checked)}
                        />
                    }
                    label={t('label.show_pw')}
                />
                <div css={btnWrapperStyle}>
                    <ButtonWithLoadingEffect
                        isLoading={isSaving}
                        css={[mainButtonSolidStyle, btnStyle]}
                        onClick={handleSavePassword}
                        disabled={
                            (needPrevPassword && !prevPassword) || !password || !confirmPassword
                        }
                    >
                        <Typography variant="subtitle2">{t('btn.save')}</Typography>
                    </ButtonWithLoadingEffect>
                    {showBackToAccountSettingBtn && (
                        <BtnBackToAccountSetting style={backBtnStyle} />
                    )}
                </div>
            </div>
        );
    }
);

export default BlockPasswordInput;

const containerStyle = css`
    width: 360px;
    > * {
        letter-spacing: 0.5px;
    }
`;

const titleStyle = css`
    color: ${neutral900};
    text-align: center;
`;

const descStyle = css`
    color: ${neutral500};
    text-align: center;
    margin-top: 24px;
`;

const btnWrapperStyle = css`
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const btnStyle = css`
    width: 60%;
`;

const backBtnStyle = `
    width: 214px;
    margin-top: 8px;
    ${above('xlg')} {
        margin-top: 24px;
    }
`;
